import { isLocalStorageSupported } from "@outschool/local-storage";
import { BASE_LOCALE } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import * as Sentry from "@outschool/sentry/dist/client";
import {
  useAnalytics,
  usePageContext,
  useTrackEvent
} from "@outschool/ui-analytics";
import { createApolloClient } from "@outschool/ui-gql/dist/apollo-v2";
import { ApolloClient } from "apollo-client";
import React from "react";
import { ApolloProvider } from "react-apollo";

import * as Env from "../../shared/Env";
import { loginPath } from "../../shared/Routes";
import * as CategoryPageVisitHistory from "../lib/CategoryPageVisitHistory";
import * as ClassPageVisitHistory from "../lib/ClassPageVisitHistory";
import typeDefs from "../lib/clientSchema";
import {
  REFRESH_TOKEN_LOCAL_STORAGE_NAME,
  useTokenContext
} from "./TokenProvider";

let localStorage = isLocalStorageSupported();

function captureException(fn: () => void) {
  try {
    fn();
  } catch (e) {
    Sentry.logException(e, {
      tags: {
        component: Component.UserAccountManagement
      }
    });
  }
}

type Context = {
  expectedClientVersion: number | undefined;
  resetClientVersion(): void;
  client?: ApolloClient<any>;
  logout(additionalContext?: object): void;
};

const GraphqlContext = React.createContext<Context>({
  expectedClientVersion: undefined,
  resetClientVersion: () => null,
  client: undefined,
  logout: () => null
});

export const GraphqlProvider: React.FC<{
  __testApolloClient?: ApolloClient<any>;
}> = ({ children, __testApolloClient }) => {
  const analytics = useAnalytics();
  const trackEvent = useTrackEvent();
  const pageContext = usePageContext();
  const pageContextRef = React.useRef(pageContext);
  const hardLogout = Env.ENABLED_FEATURE_FLAGS["ppp-hard-refresh-on-logout"];
  const { getTokens, setTokens, resetTokens } = useTokenContext();
  const expectedClientVersion = React.useRef<number | undefined>(
    Env.clientVersion
  );
  pageContextRef.current = pageContext;

  const logout = React.useCallback(
    (additionalContext?: object) => {
      if (!Env.isTest) {
        console.log("logoutCurrentUser");
      }
      captureException(() => trackEvent("logout", additionalContext));
      captureException(() => {
        analytics.reset();
      });
      captureException(() => {
        const sentry = (window as any)?.Sentry;
        if (!!sentry) {
          sentry.configureScope(scope => scope.setUser());
        }
      });
      captureException(ClassPageVisitHistory.clear);
      captureException(CategoryPageVisitHistory.clear);
      captureException(() => {
        // @ts-ignore TS(2339): Property 'Intercom' does not exist on type 'Window... Remove this comment to see the full error message
        window.Intercom?.("shutdown");
      });
      captureException(resetTokens);
      captureException(() => {
        expectedClientVersion.current = undefined;
      });
      if (hardLogout) {
        //eslint-disable-next-line no-restricted-syntax
        window.location.assign(
          Env.LOCALE === BASE_LOCALE
            ? loginPath(true)
            : "/" + Env.LOCALE + loginPath(true)
        );
      }
    },
    [analytics, trackEvent, resetTokens, hardLogout]
  );

  React.useEffect(() => {
    const listener = e => {
      if (e.key === REFRESH_TOKEN_LOCAL_STORAGE_NAME) {
        console.log(e);
      }
      if (
        e.key === REFRESH_TOKEN_LOCAL_STORAGE_NAME &&
        !e.newValue &&
        getTokens().refreshToken
      ) {
        if (Env.LOGOUT_ACROSS_TABS) {
          logout();
        } else {
          resetTokens();
        }
      }
    };
    if (localStorage) {
      window.addEventListener("storage", listener);
    }
    return () => {
      if (localStorage) {
        window.removeEventListener("storage", listener);
      }
    };
  }, [logout, getTokens, resetTokens]);

  const createClient = React.useCallback(() => {
    if (!!__testApolloClient) {
      return __testApolloClient;
    }
    const rateLimits: {
      [operationName: string]: { operationsPerMinute: number };
    } = {};
    if (Env.SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE) {
      rateLimits.SearchListings = {
        operationsPerMinute: Env.SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE
      };
    }
    const client = createApolloClient({
      name: "outschool-web-app",
      getTokens,
      setTokens,
      updateExpectedClientVersion: version => {
        expectedClientVersion.current = version;
      },
      onTokensExpired: context => {
        logout(context);
        window.location.reload();
      },
      typeDefs,
      resolvers: {},
      clientVersion: Env.clientVersion,
      locale: Env.LOCALE,
      rateLimits,
      pageContextRef
    });
    client.writeData({
      data: {
        appState: {
          __typename: "AppState",
          openModal: null,
          authTrigger: null,
          welcomeFlashMessage: null
        }
      }
    });
    return client;
  }, [__testApolloClient, getTokens, setTokens, logout]);

  const [client, setClient] = React.useState<ApolloClient<any>>(createClient());
  const [previousTokenUid, setPreviousTokenUid] = React.useState<
    string | undefined
  >(undefined);

  // Create a new GraphQL client when the token changes from string to undefined (logout)
  // to prevent leaking information between authentication state changes.
  const tokenUid = getTokens().decodedToken?.uid;
  const isLogout = tokenUid === undefined && previousTokenUid !== undefined;
  React.useEffect(() => {
    setPreviousTokenUid(tokenUid);
    if (!!__testApolloClient || !isLogout) {
      return;
    }
    setClient(createClient());
  }, [
    __testApolloClient,
    tokenUid,
    setClient,
    setPreviousTokenUid,
    isLogout,
    createClient
  ]);
  const resetClientVersion = React.useCallback(() => {
    expectedClientVersion.current = undefined;
  }, []);

  return (
    <GraphqlContext.Provider
      value={{
        expectedClientVersion: expectedClientVersion.current,
        resetClientVersion,
        client,
        logout
      }}
    >
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </GraphqlContext.Provider>
  );
};

export const useGraphqlContext = () => {
  return React.useContext(GraphqlContext);
};

export function withGraphqlContext(DecoratedComponent) {
  const displayName = DecoratedComponent.displayName || "Component";

  return class GraphqlContextWrapper extends React.Component {
    static displayName = `GraphqlContextWrapper(${displayName})`;

    render() {
      return (
        <GraphqlContext.Consumer>
          {context => <DecoratedComponent {...context} {...this.props} />}
        </GraphqlContext.Consumer>
      );
    }
  };
}
